import { useState } from "react";
import { useCreate, useGetIdentity, useGetList, useNotify } from "ra-core";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

import type { Agency, AgencyPortalUserRole, Sale } from "../types";
import { isStaffEmail } from "./staffShadowAccounts";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const ROLES: { id: AgencyPortalUserRole; label: string; hint: string }[] = [
  { id: "owner", label: "Owner", hint: "manages team + brand" },
  { id: "member", label: "Member", hint: "manages brand" },
];

/**
 * Grant portal access: inserts the allowlist row the agency portal's
 * invite-only sign-in checks. No email is sent from here — the user simply
 * signs in at the portal with this address and receives a magic link.
 */
export const InvitePortalUserDialog = ({
  agency,
  open,
  onOpenChange,
  onCreated,
}: {
  agency: Agency;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: () => void;
}) => {
  const notify = useNotify();
  const { identity } = useGetIdentity();
  const [create, { isPending: isSaving }] = useCreate();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<AgencyPortalUserRole>("member");
  // B4.1: the CRM staff roster, to recognise a staff address up front (the
  // database flags it anyway; this also covers a DB without the new trigger).
  const { data: roster } = useGetList<Sale>(
    "sales",
    { pagination: { page: 1, perPage: 500 } },
    { enabled: open, staleTime: 5 * 60 * 1000 },
  );

  const emailValid = EMAIL_RE.test(email.trim());
  const staffAddress = emailValid && isStaffEmail(email, roster ?? []);

  const reset = () => {
    setEmail("");
    setDisplayName("");
    setRole("member");
  };

  const save = () => {
    if (!emailValid) {
      return;
    }
    create(
      "agency_portal_users",
      {
        data: {
          agency_id: agency.id,
          email: email.trim().toLowerCase(),
          display_name: displayName.trim() || null,
          role,
          invited_by_sales_id: identity?.id ?? null,
          ...(staffAddress ? { is_staff: true } : {}),
        },
      },
      {
        onSuccess: () => {
          notify(
            `${email.trim()} can now sign in to the agency portal (magic link).`,
            { type: "info" },
          );
          onCreated();
          onOpenChange(false);
          reset();
        },
        onError: (error: unknown) => {
          const message = String(
            (error as { message?: string; code?: string })?.message ?? error,
          );
          const code = (error as { code?: string })?.code;
          if (message.includes("duplicate") || code === "23505") {
            notify("That email already has portal access.", {
              type: "warning",
            });
          } else {
            notify("Could not invite the portal user — please try again.", {
              type: "warning",
            });
          }
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          reset();
        }
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite portal user to {agency.name}</DialogTitle>
          <DialogDescription>
            They sign in at the agency portal with this email and get a magic
            link — no password, and no access to the CRM.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Input
            placeholder="email@agency.com"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
          {email && !emailValid && (
            <p className="text-sm text-destructive">Enter a valid email.</p>
          )}
          {staffAddress && (
            <p className="text-xs text-muted-foreground">
              Echoflicks staff address: this login is hidden from the
              agency&apos;s Team page, and its sign-ins never hand a prospect
              studio over to the client.
            </p>
          )}
          <Input
            placeholder="Name (optional)"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
          />
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Role:</span>
            {ROLES.map((option) => (
              <Button
                key={option.id}
                type="button"
                size="sm"
                variant={role === option.id ? "default" : "outline"}
                onClick={() => setRole(option.id)}
                title={option.hint}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={!emailValid || isSaving}>
            Invite
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
